<script setup>
import { isSupportRole } from '../utils/runner-role.js'
import { metersToKm } from '../utils/live-splits.js'
import { onUnmounted, ref, computed, watch, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from 'vue-router'
import {
  Chart,
  BarElement,
  BarController,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'

Chart.register(
  BarElement, BarController,
  LineController, LineElement, PointElement,
  CategoryScale, LinearScale,
  Tooltip, Legend, Filler
)

const PRIMARY_COLOR = '#01438f'
const SECONDARY_COLOR = '#1B2A6B'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
const runnerNumber = route.params.runnerNumber
resultsStore.fetchResults(eventAlias)

// ── histogram controls ─────────────────────────────────────────────────────
const bucketSize = ref(10) // minutes
const selectedCheckpointId = ref(null)

// ── chart instances ────────────────────────────────────────────────────────
const histogramChart = ref(null)
const progressionChart = ref(null)
const paceChart = ref(null)
const finishTimeChart = ref(null)
const histogramCanvasRef = ref(null)
const progressionCanvasRef = ref(null)
const paceCanvasRef = ref(null)
const finishTimeChartCanvasRef = ref(null)

// ═══════════════════════════════════════════════════════════════════════════
// Core utility: resolveFinishTime
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Returns the authoritative finish time (ISO string) for a runner:
 *   1. runner.finish_time if present and not null
 *   2. Earliest punch timestamp at a finish marker (type 8 or 93)
 *   3. null if neither is available
 */
function resolveFinishTime(runner, markersMap) {
  if (runner.finish_time != null) return runner.finish_time
  const markersArray = Object.values(markersMap)
  const finishMarkerIds = markersArray
    .filter(m => m.type === 8 || m.type === 93)
    .map(m => m.id)
  let earliest = null
  for (const markerId of finishMarkerIds) {
    const split = runner.splits?.[markerId]
    if (split?.time) {
      if (!earliest || new Date(split.time) < new Date(earliest)) {
        earliest = split.time
      }
    }
  }
  return earliest
}

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function formatElapsed(ms) {
  if (!ms || ms <= 0) return '--'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

function markerLabel(text) {
  return (text || '').replace(/\|/g, ' ')
}

function ordinal(n) {
  if (n == null) return ''
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

function elapsedMs(runner, splitTime) {
  if (!runner.start_time || !splitTime) return null
  const diff = new Date(splitTime) - new Date(runner.start_time)
  return diff > 0 ? diff : null
}

function formatHHMM(ms) {
  if (!ms || ms <= 0) return '--'
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m}:00`
}

/**
 * Dense ranking: runners with identical elapsed times share the same rank.
 * Returns a Map<runnerId, rank>.
 */
function denseRank(runners, getScore) {
  const sorted = [...runners]
    .filter(r => getScore(r) != null)
    .sort((a, b) => getScore(a) - getScore(b))
  const rankMap = new Map()
  let rank = 1
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && getScore(sorted[i]) !== getScore(sorted[i - 1])) {
      rank = i + 1
    }
    rankMap.set(sorted[i].id, rank)
  }
  return rankMap
}

// ═══════════════════════════════════════════════════════════════════════════
// Runner lookup
// ═══════════════════════════════════════════════════════════════════════════

const runner = computed(() =>
  resultsStore.results.find(r => String(r.number) === String(runnerNumber)) || null
)

const markersMap = computed(() => resultsStore.markersMap)
const categories = computed(() => resultsStore.categories)

// ═══════════════════════════════════════════════════════════════════════════
// Runner status determination
// ═══════════════════════════════════════════════════════════════════════════

const finishMarkerIds = computed(() => {
  const ids = new Set()
  Object.values(markersMap.value).forEach(m => {
    if (m.type === 8 || m.type === 93) ids.add(m.id)
  })
  return ids
})

const runnerFinishTime = computed(() => {
  if (!runner.value) return null
  return resolveFinishTime(runner.value, markersMap.value)
})

const runnerStatus = computed(() => {
  if (!runner.value) return null
  const ft = runnerFinishTime.value
  if (ft != null && runner.value.start_time != null) return 'FINISHED'
  // Check for non-finish splits
  const splits = runner.value.splits || {}
  const hasNonFinishSplit = Object.keys(splits).some(
    mid => !finishMarkerIds.value.has(Number(mid))
  )
  return hasNonFinishSplit ? 'IN_PROGRESS' : 'DNF'
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 1 — Runner identity header
// ═══════════════════════════════════════════════════════════════════════════

const runnerCategory = computed(() => {
  if (!runner.value) return ''
  const cat = categories.value.find(c => c.id === runner.value.category_id)
  return cat ? cat.name : ''
})

const finishElapsedMs = computed(() => {
  if (!runner.value || runnerStatus.value !== 'FINISHED') return null
  return elapsedMs(runner.value, runnerFinishTime.value)
})

/** All FINISHED runners sorted by elapsed time (ascending) */
const finishedRunners = computed(() => {
  return resultsStore.results
    .filter(r => {
      if (isSupportRole(r)) return false
      const ft = resolveFinishTime(r, markersMap.value)
      return ft != null && r.start_time != null
    })
    .map(r => ({ ...r, _ft: elapsedMs(r, resolveFinishTime(r, markersMap.value)) }))
    .filter(r => r._ft != null)
})

const overallRankMap = computed(() => denseRank(finishedRunners.value, r => r._ft))

const overallRank = computed(() => {
  if (!runner.value || runnerStatus.value !== 'FINISHED') return null
  return overallRankMap.value.get(runner.value.id) ?? null
})

const categoryRankMap = computed(() => {
  if (!runner.value || !runner.value.category_id) return new Map()
  const catRunners = finishedRunners.value.filter(r => r.category_id === runner.value.category_id)
  return denseRank(catRunners, r => r._ft)
})

const categoryRank = computed(() => {
  if (!runner.value || runnerStatus.value !== 'FINISHED') return null
  return categoryRankMap.value.get(runner.value.id) ?? null
})

/** Last checkpoint reached (for IN PROGRESS runners) */
const lastCheckpointInfo = computed(() => {
  if (!runner.value || runnerStatus.value !== 'IN_PROGRESS') return null
  const splits = runner.value.splits || {}
  const markers = Object.values(markersMap.value).sort((a, b) => a.sort - b.sort)
  let lastMarker = null
  let lastSplit = null
  for (const marker of markers) {
    if (finishMarkerIds.value.has(marker.id)) continue
    const split = splits[marker.id]
    if (split) { lastMarker = marker; lastSplit = split }
  }
  if (!lastMarker || !lastSplit) return null
  const ms = elapsedMs(runner.value, lastSplit.time)
  return { name: markerLabel(lastMarker.text), elapsed: formatElapsed(ms) }
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 2 — Arrival histogram per checkpoint
// ═══════════════════════════════════════════════════════════════════════════

/** Checkpoints the runner has a punch at, in course order */
const visitedCheckpoints = computed(() => {
  if (!runner.value) return []
  const splits = runner.value.splits || {}
  const markers = Object.values(markersMap.value).sort((a, b) => a.sort - b.sort)
  return markers.filter(m => splits[m.id] != null)
})

watch(visitedCheckpoints, (cps) => {
  if (cps.length && selectedCheckpointId.value === null) {
    selectedCheckpointId.value = cps[cps.length - 1]?.id ?? null
  }
}, { immediate: true })

/** Build histogram data for a given marker, with runner's bucket highlighted */
function buildHistogramData(markerId, bktSize) {
  const isFinishMarker = finishMarkerIds.value.has(markerId)
  const allRunners = resultsStore.results.filter(r => {
    if (isSupportRole(r)) return false
    if (!r.start_time) return false
    return true
  })

  const elapsed = [] // {ms, runnerId}
  allRunners.forEach(r => {
    let timeStr
    if (isFinishMarker) {
      timeStr = resolveFinishTime(r, markersMap.value)
    } else {
      timeStr = r.splits?.[markerId]?.time ?? null
    }
    const ms = timeStr ? elapsedMs(r, timeStr) : null
    if (ms) elapsed.push({ ms, runnerId: r.id })
  })

  if (elapsed.length === 0) return { labels: [], data: [], colors: [], runnerBucketIndex: -1, runnerElapsedMin: null }

  const minutes = elapsed.map(e => e.ms / 60000)
  const maxMin = Math.max(...minutes)
  const numBuckets = Math.ceil(maxMin / bktSize) + 1
  const counts = new Array(numBuckets).fill(0)

  elapsed.forEach(({ ms }) => {
    const m = ms / 60000
    const idx = Math.floor(m / bktSize)
    if (idx < counts.length) {
      counts[idx]++
    }
  })

  // Find runner's bucket
  let runnerBucketIndex = -1
  let runnerElapsedMin = null
  if (runner.value) {
    let runnerTimeStr
    if (isFinishMarker) {
      runnerTimeStr = resolveFinishTime(runner.value, markersMap.value)
    } else {
      runnerTimeStr = runner.value.splits?.[markerId]?.time ?? null
    }
    const runnerMs = runnerTimeStr ? elapsedMs(runner.value, runnerTimeStr) : null
    if (runnerMs) {
      runnerElapsedMin = runnerMs / 60000
      runnerBucketIndex = Math.min(Math.floor(runnerElapsedMin / bktSize), numBuckets - 1)
    }
  }

  const labels = counts.map((_, i) => `${i * bktSize}–${(i + 1) * bktSize} min`)
  const colors = counts.map((_, i) =>
    i === runnerBucketIndex ? PRIMARY_COLOR : 'rgba(27, 42, 107, 0.3)'
  )

  // Trim trailing zeros
  let last = counts.length - 1
  while (last > 0 && counts[last] === 0) last--

  return {
    labels: labels.slice(0, last + 1),
    data: counts.slice(0, last + 1),
    colors: colors.slice(0, last + 1),
    runnerBucketIndex: runnerBucketIndex <= last ? runnerBucketIndex : -1,
    runnerElapsedMin,
    bktSize,
  }
}

function renderHistogram() {
  if (!histogramCanvasRef.value || selectedCheckpointId.value === null) return
  if (!runner.value || runnerStatus.value === 'DNF') return

  const { labels, data, colors, runnerBucketIndex, runnerElapsedMin, bktSize } =
    buildHistogramData(selectedCheckpointId.value, bucketSize.value)

  if (histogramChart.value) {
    histogramChart.value.destroy()
    histogramChart.value = null
  }
  if (!labels.length) return

  const marker = markersMap.value[selectedCheckpointId.value]
  const markerName = marker ? markerLabel(marker.text) : ''

  // Compute runner's overall rank at this checkpoint for tooltip
  let runnerRankAtCheckpoint = null
  if (runnerBucketIndex >= 0 && runner.value) {
    const isFinish = finishMarkerIds.value.has(selectedCheckpointId.value)
    const allAtCheckpoint = resultsStore.results.filter(r => {
      if (isSupportRole(r) || !r.start_time) return false
      const t = isFinish
        ? resolveFinishTime(r, markersMap.value)
        : r.splits?.[selectedCheckpointId.value]?.time
      return !!t
    })
    const rankMap = denseRank(
      allAtCheckpoint.map(r => {
        const t = isFinish
          ? resolveFinishTime(r, markersMap.value)
          : r.splits?.[selectedCheckpointId.value]?.time
        return { ...r, _t: elapsedMs(r, t) }
      }).filter(r => r._t != null),
      r => r._t
    )
    runnerRankAtCheckpoint = rankMap.get(runner.value.id) ?? null
  }

  // Custom plugin: dashed vertical line + annotation
  const runnerAnnotationPlugin = {
    id: 'runnerAnnotation',
    afterDraw(chart) {
      if (runnerBucketIndex < 0 || runnerElapsedMin == null) return
      const meta = chart.getDatasetMeta(0)
      const bar = meta.data[runnerBucketIndex]
      if (!bar) return
      const { ctx } = chart
      const yScale = chart.scales.y

      // Calculate exact x position within the bar
      const barLeft = bar.x - bar.width / 2
      const barRight = bar.x + bar.width / 2
      const fraction = (runnerElapsedMin - runnerBucketIndex * bktSize) / bktSize
      const xPos = barLeft + fraction * (barRight - barLeft)

      // Dashed vertical line
      ctx.save()
      ctx.strokeStyle = PRIMARY_COLOR
      ctx.lineWidth = 2
      ctx.setLineDash([6, 4])
      ctx.beginPath()
      ctx.moveTo(xPos, yScale.top)
      ctx.lineTo(xPos, yScale.bottom)
      ctx.stroke()

      // Triangle marker ▲
      ctx.fillStyle = PRIMARY_COLOR
      ctx.setLineDash([])
      ctx.beginPath()
      ctx.moveTo(xPos, yScale.bottom + 12)
      ctx.lineTo(xPos - 6, yScale.bottom + 22)
      ctx.lineTo(xPos + 6, yScale.bottom + 22)
      ctx.closePath()
      ctx.fill()

      // Text label
      const label = `Ty — ${formatHHMM(runnerElapsedMin * 60000)}`
      ctx.font = 'bold 11px sans-serif'
      ctx.fillStyle = PRIMARY_COLOR
      ctx.textAlign = 'center'
      const textY = yScale.top - 6
      ctx.fillText(label, Math.min(Math.max(xPos, 60), chart.width - 60), textY)
      ctx.restore()
    }
  }

  histogramChart.value = new Chart(histogramCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: `Arrivals – ${markerName}`,
        data,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24, bottom: 28 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const idx = items[0].dataIndex
              return `${idx * bktSize}–${(idx + 1) * bktSize} min`
            },
            label: (item) => {
              const idx = item.dataIndex
              const n = item.raw
              const isRunnerBucket = idx === runnerBucketIndex
              const lines = [`${n} participant${n !== 1 ? 's' : ''}`]
              if (isRunnerBucket && runnerElapsedMin != null) {
                const rankStr = runnerRankAtCheckpoint != null ? ` · ${ordinal(runnerRankAtCheckpoint)} overall` : ''
                lines.push(`You arrived at ${formatHHMM(runnerElapsedMin * 60000)}${rankStr}`)
              }
              return lines
            },
          },
        },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
        y: { beginAtZero: true, ticks: { stepSize: 1 } },
      },
    },
    plugins: [runnerAnnotationPlugin],
  })
}

watch(
  [selectedCheckpointId, bucketSize, () => resultsStore.results.length],
  () => { nextTick(renderHistogram) }
)

// ═══════════════════════════════════════════════════════════════════════════
// Section 3 — Checkpoint split table
// ═══════════════════════════════════════════════════════════════════════════

const splitTableRows = computed(() => {
  if (!runner.value) return []
  const allRunners = resultsStore.results.filter(r => !isSupportRole(r) && r.start_time)
  const markers = Object.values(markersMap.value).sort((a, b) => a.sort - b.sort)
  const catRunners = runner.value.category_id
    ? allRunners.filter(r => r.category_id === runner.value.category_id)
    : []

  const rows = []
  let prevElapsedMs = null

  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i]
    const isFinish = finishMarkerIds.value.has(marker.id)

    // Compute elapsed for THIS runner at this checkpoint
    let myTimeStr
    if (isFinish) {
      myTimeStr = resolveFinishTime(runner.value, markersMap.value)
    } else {
      myTimeStr = runner.value.splits?.[marker.id]?.time ?? null
    }
    const myMs = myTimeStr ? elapsedMs(runner.value, myTimeStr) : null
    const reached = myMs != null

    // Segment time (from previous checkpoint)
    const segmentMs = (reached && prevElapsedMs != null) ? myMs - prevElapsedMs : null

    // Overall rank at this checkpoint
    let rank = null
    let catRankVal = null
    let prevRank = rows.length > 0 ? rows[rows.length - 1].rank : null
    let delta = null

    if (reached) {
      const withElapsed = allRunners
        .map(r => {
          let t
          if (isFinish) {
            t = resolveFinishTime(r, markersMap.value)
          } else {
            t = r.splits?.[marker.id]?.time ?? null
          }
          const ms = t ? elapsedMs(r, t) : null
          return { id: r.id, ms }
        })
        .filter(r => r.ms != null)

      const rankMap = denseRank(withElapsed, r => r.ms)
      rank = rankMap.get(runner.value.id) ?? null

      // Category rank
      if (catRunners.length > 0 && runner.value.category_id) {
        const catWithElapsed = catRunners
          .map(r => {
            let t
            if (isFinish) {
              t = resolveFinishTime(r, markersMap.value)
            } else {
              t = r.splits?.[marker.id]?.time ?? null
            }
            const ms = t ? elapsedMs(r, t) : null
            return { id: r.id, ms }
          })
          .filter(r => r.ms != null)
        const catRankMap = denseRank(catWithElapsed, r => r.ms)
        catRankVal = catRankMap.get(runner.value.id) ?? null
      }

      // Position delta vs previous checkpoint
      if (prevRank != null && rank != null) {
        delta = prevRank - rank // positive = gained positions
      }
    }

    rows.push({
      name: markerLabel(marker.text),
      elapsed: reached ? formatElapsed(myMs) : '—',
      segment: segmentMs != null ? formatElapsed(segmentMs) : '—',
      rank: reached ? rank : null,
      catRank: reached ? catRankVal : null,
      delta: reached ? delta : null,
      reached,
    })

    if (reached) prevElapsedMs = myMs
  }

  return rows
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 4 — Position progression chart
// ═══════════════════════════════════════════════════════════════════════════

const progressionPoints = computed(() => {
  if (!runner.value || runnerStatus.value === 'DNF') return []
  return splitTableRows.value
    .filter(row => row.reached && row.rank != null)
    .map(row => ({ label: row.name, rank: row.rank }))
})

function renderProgressionChart() {
  if (!progressionCanvasRef.value) return
  const points = progressionPoints.value
  if (points.length < 2) return

  if (progressionChart.value) {
    progressionChart.value.destroy()
    progressionChart.value = null
  }

  const labels = points.map(p => p.label)
  const data = points.map(p => p.rank)
  const maxRank = Math.max(...data)

  progressionChart.value = new Chart(progressionCanvasRef.value, {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Overall rank',
        data,
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR,
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.1,
        datalabels: null,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => `Rank: ${item.raw}`,
          },
        },
      },
      scales: {
        y: {
          reverse: true,
          beginAtZero: false,
          min: 1,
          max: maxRank + 1,
          title: { display: true, text: 'Rank (1 = best)' },
          ticks: { stepSize: 1 },
        },
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
      },
    },
    plugins: [{
      id: 'rankLabels',
      afterDatasetsDraw(chart) {
        const meta = chart.getDatasetMeta(0)
        const { ctx } = chart
        ctx.save()
        ctx.fillStyle = PRIMARY_COLOR
        ctx.font = 'bold 12px sans-serif'
        ctx.textAlign = 'center'
        meta.data.forEach((point, i) => {
          ctx.fillText(String(data[i]), point.x, point.y - 10)
        })
        ctx.restore()
      }
    }],
  })
}

watch(progressionPoints, () => { nextTick(renderProgressionChart) }, { immediate: true })

// ═══════════════════════════════════════════════════════════════════════════
// Section 5 — Pace per segment (only if distances available)
// ═══════════════════════════════════════════════════════════════════════════

const hasDistances = computed(() => {
  const markers = Object.values(markersMap.value)
  return markers.length > 1 && markers.every(m => m.distance != null && m.distance > 0)
})

const paceData = computed(() => {
  if (!hasDistances.value || !runner.value || runnerStatus.value === 'DNF') return null

  const markers = Object.values(markersMap.value).sort((a, b) => a.sort - b.sort)
  const allRunners = resultsStore.results.filter(r => !isSupportRole(r) && r.start_time)
  const segments = []

  for (let i = 1; i < markers.length; i++) {
    const from = markers[i - 1]
    const to = markers[i]
    const segDist = metersToKm(to.distance - from.distance) // markers.distance w metrach → km
    if (!segDist || segDist <= 0) return null

    const label = `${markerLabel(from.text)} → ${markerLabel(to.text)}`

    // Runner's segment time
    const isFinish = finishMarkerIds.value.has(to.id)
    const toTimeStr = isFinish
      ? resolveFinishTime(runner.value, markersMap.value)
      : runner.value.splits?.[to.id]?.time
    const fromTimeStr = finishMarkerIds.value.has(from.id)
      ? resolveFinishTime(runner.value, markersMap.value)
      : runner.value.splits?.[from.id]?.time

    if (!toTimeStr || !fromTimeStr) {
      segments.push({ label, runnerPace: null, q1: null, median: null, q3: null })
      continue
    }
    const segMs = new Date(toTimeStr) - new Date(fromTimeStr)
    const runnerPace = segMs > 0 ? (segMs / 60000) / segDist : null

    // Field paces
    const fieldPaces = []
    allRunners.forEach(r => {
      const tTo = isFinish
        ? resolveFinishTime(r, markersMap.value)
        : r.splits?.[to.id]?.time
      const tFrom = finishMarkerIds.value.has(from.id)
        ? resolveFinishTime(r, markersMap.value)
        : r.splits?.[from.id]?.time
      if (!tTo || !tFrom) return
      const ms = new Date(tTo) - new Date(tFrom)
      if (ms > 0) fieldPaces.push((ms / 60000) / segDist)
    })

    if (fieldPaces.length === 0) {
      segments.push({ label, runnerPace, q1: null, median: null, q3: null })
      continue
    }

    fieldPaces.sort((a, b) => a - b)
    const n = fieldPaces.length
    const median = n % 2 === 0
      ? (fieldPaces[n / 2 - 1] + fieldPaces[n / 2]) / 2
      : fieldPaces[Math.floor(n / 2)]
    const q1 = fieldPaces[Math.floor(n * 0.25)]
    const q3 = fieldPaces[Math.floor(n * 0.75)]

    segments.push({ label, runnerPace, q1, median, q3 })
  }

  return segments
})

function renderPaceChart() {
  if (!paceCanvasRef.value || !paceData.value) return
  const segments = paceData.value.filter(s => s.runnerPace != null)
  if (segments.length < 2) return

  if (paceChart.value) {
    paceChart.value.destroy()
    paceChart.value = null
  }

  const labels = segments.map(s => s.label)
  const runnerPaces = segments.map(s => s.runnerPace)
  const medians = segments.map(s => s.median)
  const q1s = segments.map(s => s.q1)
  const q3s = segments.map(s => s.q3)

  paceChart.value = new Chart(paceCanvasRef.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'IQR lower',
          data: q1s,
          borderColor: 'transparent',
          backgroundColor: `rgba(27, 42, 107, 0.10)`,
          fill: '+1',
          pointRadius: 0,
        },
        {
          label: 'IQR upper',
          data: q3s,
          borderColor: 'transparent',
          backgroundColor: `rgba(27, 42, 107, 0.10)`,
          fill: false,
          pointRadius: 0,
        },
        {
          label: 'Field median',
          data: medians,
          borderColor: SECONDARY_COLOR,
          backgroundColor: 'transparent',
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 3,
          tension: 0.1,
        },
        {
          label: 'Your pace',
          data: runnerPaces,
          borderColor: PRIMARY_COLOR,
          backgroundColor: PRIMARY_COLOR,
          borderWidth: 3,
          pointRadius: 5,
          tension: 0.1,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'top' },
        tooltip: {
          callbacks: {
            label: (item) => {
              const val = item.raw
              if (val == null) return ''
              return `${item.dataset.label}: ${val.toFixed(2)} min/km`
            },
          },
        },
      },
      scales: {
        y: {
          title: { display: true, text: 'Pace (min/km)' },
        },
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
      },
    },
  })
}

watch(paceData, () => { nextTick(renderPaceChart) }, { immediate: true })

// ═══════════════════════════════════════════════════════════════════════════
// Section 6 — Category comparison
// ═══════════════════════════════════════════════════════════════════════════

const categoryStats = computed(() => {
  if (!runner.value || runnerStatus.value !== 'FINISHED' || !runner.value.category_id) return null
  const catFinishers = finishedRunners.value.filter(r => r.category_id === runner.value.category_id)
  if (catFinishers.length < 3) return null

  const sorted = [...catFinishers].sort((a, b) => a._ft - b._ft)
  const myFt = finishElapsedMs.value
  if (!myFt) return null

  const avg = sorted.reduce((s, r) => s + r._ft, 0) / sorted.length
  const n = sorted.length
  const mid = Math.floor(n / 2)
  const median = n % 2 === 0 ? (sorted[mid - 1]._ft + sorted[mid]._ft) / 2 : sorted[mid]._ft

  const myIdx = sorted.findIndex(r => r.id === runner.value.id)
  const aheadRunner = myIdx > 0 ? sorted[myIdx - 1] : null
  const behindRunner = myIdx >= 0 && myIdx < sorted.length - 1 ? sorted[myIdx + 1] : null

  return {
    count: n,
    avg: formatElapsed(avg),
    median: formatElapsed(median),
    diffFromAvg: myFt - avg,
    diffFromMedian: myFt - median,
    aheadName: aheadRunner ? aheadRunner.name : null,
    aheadGap: aheadRunner ? myFt - aheadRunner._ft : null,
    aheadRank: aheadRunner ? (myIdx - 1 + 1) : null, // aheadRunner is at index myIdx-1, rank = myIdx-1+1 = myIdx
    behindName: behindRunner ? behindRunner.name : null,
    behindGap: behindRunner ? behindRunner._ft - myFt : null,
  }
})

// ═══════════════════════════════════════════════════════════════════════════
// Section 7 — Finish time comparison chart
// ═══════════════════════════════════════════════════════════════════════════

function renderFinishTimeChart() {
  if (!finishTimeChartCanvasRef.value) return
  if (!runner.value || runnerStatus.value !== 'FINISHED') return

  const sorted = [...finishedRunners.value].sort((a, b) => a._ft - b._ft)
  if (sorted.length < 2) return

  if (finishTimeChart.value) {
    finishTimeChart.value.destroy()
    finishTimeChart.value = null
  }

  // Determine bucket size based on the range between first and last finish time
  const minMs = sorted[0]._ft
  const maxMs = sorted[sorted.length - 1]._ft
  const rangeMin = (maxMs - minMs) / 60000
  const NICE_BUCKETS = [1, 5, 10, 15, 30, 60, 120]
  const TARGET_BUCKETS = 30
  let bktSize = NICE_BUCKETS[NICE_BUCKETS.length - 1]
  for (const b of NICE_BUCKETS) {
    if (rangeMin / b <= TARGET_BUCKETS) { bktSize = b; break }
  }

  // Build buckets aligned to multiples of bktSize (in minutes from start)
  const minBucketMin = Math.floor((minMs / 60000) / bktSize) * bktSize
  const numBuckets = Math.ceil((maxMs / 60000 - minBucketMin) / bktSize) + 1
  const counts = new Array(numBuckets).fill(0)
  sorted.forEach(r => {
    const idx = Math.floor((r._ft / 60000 - minBucketMin) / bktSize)
    if (idx >= 0 && idx < counts.length) counts[idx]++
  })

  // Find runner's bucket and exact position within it
  const runnerMs = finishElapsedMs.value
  const runnerMin = runnerMs != null ? runnerMs / 60000 : null
  let runnerBucketIndex = -1
  if (runnerMin != null) {
    runnerBucketIndex = Math.floor((runnerMin - minBucketMin) / bktSize)
    if (runnerBucketIndex < 0 || runnerBucketIndex >= counts.length) runnerBucketIndex = -1
  }

  // Trim trailing empty buckets
  let last = counts.length - 1
  while (last > 0 && counts[last] === 0) last--
  const trimmedCounts = counts.slice(0, last + 1)
  const trimmedBucketIndex = runnerBucketIndex <= last ? runnerBucketIndex : -1

  const labels = trimmedCounts.map((_, i) => {
    const startMs = (minBucketMin + i * bktSize) * 60000
    const endMs = (minBucketMin + (i + 1) * bktSize) * 60000
    return `${formatHHMM(startMs)}–${formatHHMM(endMs)}`
  })
  const colors = trimmedCounts.map((_, i) =>
    i === trimmedBucketIndex ? PRIMARY_COLOR : 'rgba(27, 42, 107, 0.3)'
  )

  // Annotation plugin: dashed vertical line at runner's exact finish time
  const runnerAnnotationPlugin = {
    id: 'finishTimeAnnotation',
    afterDraw(chart) {
      if (trimmedBucketIndex < 0 || runnerMin == null) return
      const meta = chart.getDatasetMeta(0)
      const bar = meta.data[trimmedBucketIndex]
      if (!bar) return
      const { ctx } = chart
      const yScale = chart.scales.y

      const barLeft = bar.x - bar.width / 2
      const barRight = bar.x + bar.width / 2
      const fraction = (runnerMin - (minBucketMin + trimmedBucketIndex * bktSize)) / bktSize
      const xPos = Math.min(Math.max(barLeft + fraction * (barRight - barLeft), barLeft), barRight)

      ctx.save()
      ctx.strokeStyle = PRIMARY_COLOR
      ctx.lineWidth = 2
      ctx.setLineDash([6, 4])
      ctx.beginPath()
      ctx.moveTo(xPos, yScale.top)
      ctx.lineTo(xPos, yScale.bottom)
      ctx.stroke()

      ctx.fillStyle = PRIMARY_COLOR
      ctx.setLineDash([])
      ctx.beginPath()
      ctx.moveTo(xPos, yScale.bottom + 12)
      ctx.lineTo(xPos - 6, yScale.bottom + 22)
      ctx.lineTo(xPos + 6, yScale.bottom + 22)
      ctx.closePath()
      ctx.fill()

      const label = `Ty — ${formatElapsed(runnerMs)}`
      ctx.font = 'bold 11px sans-serif'
      ctx.fillStyle = PRIMARY_COLOR
      ctx.textAlign = 'center'
      ctx.fillText(label, Math.min(Math.max(xPos, 60), chart.width - 60), yScale.top - 6)
      ctx.restore()
    }
  }

  finishTimeChart.value = new Chart(finishTimeChartCanvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Finishers',
        data: trimmedCounts,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 24, bottom: 28 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => labels[items[0].dataIndex] ?? '',
            label: (item) => {
              const idx = item.dataIndex
              const n = item.raw
              const lines = [`${n} finisher${n !== 1 ? 's' : ''}`]
              if (idx === trimmedBucketIndex && runnerMin != null) {
                lines.push(`Ty — ${formatElapsed(runnerMs)}`)
              }
              return lines
            },
          },
        },
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 30 } },
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1 },
          title: { display: true, text: 'Liczba zawodników' },
        },
      },
    },
    plugins: [runnerAnnotationPlugin],
  })
}

watch(
  [finishedRunners, () => runner.value?.id],
  () => { nextTick(renderFinishTimeChart) },
  { immediate: true }
)

// ═══════════════════════════════════════════════════════════════════════════
// Lifecycle
// ═══════════════════════════════════════════════════════════════════════════

onUnmounted(() => {
  histogramChart.value?.destroy()
  progressionChart.value?.destroy()
  paceChart.value?.destroy()
  finishTimeChart.value?.destroy()
})
</script>

<template>
  <div class="runner-stats-page container-fluid py-3">

    <!-- Not found -->
    <div v-if="resultsStore.results.length > 0 && !runner" class="alert alert-warning">
      Runner #{{ runnerNumber }} not found in this event.
    </div>

    <!-- Loading -->
    <div v-else-if="!runner" class="text-center py-5 text-muted">
      Loading…
    </div>

    <template v-else>
      <!-- ── Back link ──────────────────────────────────────────────────── -->
      <div class="mb-3">
        <router-link :to="`/stats/${eventAlias}`" class="btn btn-sm btn-outline-secondary">
          ← Event stats
        </router-link>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 1 — Runner identity header                            -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-start gap-3">
            <!-- Bib badge -->
            <div class="bib-badge d-flex align-items-center justify-content-center">
              {{ runner.number }}
            </div>
            <div class="flex-grow-1">
              <h2 class="mb-1">{{ runner.name }}</h2>
              <div class="d-flex flex-wrap gap-2 align-items-center mb-2">
                <span v-if="runnerCategory" class="badge bg-secondary">{{ runnerCategory }}</span>
                <!-- Status badge -->
                <span
                  class="badge fs-6"
                  :class="{
                    'status-finished': runnerStatus === 'FINISHED',
                    'status-in-progress': runnerStatus === 'IN_PROGRESS',
                    'status-dnf': runnerStatus === 'DNF',
                  }"
                >
                  {{ runnerStatus === 'FINISHED' ? 'FINISHED' : runnerStatus === 'IN_PROGRESS' ? 'IN PROGRESS' : 'DNF' }}
                </span>
              </div>

              <!-- FINISHED details -->
              <template v-if="runnerStatus === 'FINISHED'">
                <div class="d-flex flex-wrap gap-4 mt-2">
                  <div class="stat-block">
                    <div class="stat-value text-primary-brand">{{ formatElapsed(finishElapsedMs) }}</div>
                    <div class="stat-label">Finish time</div>
                  </div>
                  <div class="stat-block" v-if="overallRank != null">
                    <div class="stat-value">{{ overallRank }}</div>
                    <div class="stat-label">Overall rank</div>
                  </div>
                  <div class="stat-block" v-if="categoryRank != null && runnerCategory">
                    <div class="stat-value">{{ categoryRank }}</div>
                    <div class="stat-label">{{ runnerCategory }} rank</div>
                  </div>
                </div>
              </template>

              <!-- IN PROGRESS details -->
              <template v-else-if="runnerStatus === 'IN_PROGRESS'">
                <div v-if="lastCheckpointInfo" class="mt-2 text-muted">
                  Last checkpoint: <strong>{{ lastCheckpointInfo.name }}</strong>
                  at <strong>{{ lastCheckpointInfo.elapsed }}</strong> from start
                </div>
              </template>

              <!-- DNF -->
              <template v-else>
                <div class="mt-2 text-muted fst-italic">
                  No timing data recorded for this participant.
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 2 — Arrival histogram                                  -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div v-if="runnerStatus !== 'DNF' && visitedCheckpoints.length > 0" class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Arrival distribution</div>
        <div class="card-body">
          <!-- Controls -->
          <div class="d-flex flex-wrap gap-3 mb-3 align-items-center">
            <!-- Checkpoint selector: dropdown on mobile, tabs on desktop -->
            <div class="checkpoint-tabs d-none d-md-flex flex-wrap gap-1">
              <button
                v-for="cp in visitedCheckpoints"
                :key="cp.id"
                class="btn btn-sm"
                :class="selectedCheckpointId === cp.id ? 'btn-primary-brand' : 'btn-outline-secondary'"
                @click="selectedCheckpointId = cp.id"
              >{{ markerLabel(cp.text) }}</button>
            </div>
            <div class="d-md-none w-100">
              <select
                v-model.number="selectedCheckpointId"
                class="form-select form-select-sm"
              >
                <option v-for="cp in visitedCheckpoints" :key="cp.id" :value="cp.id">
                  {{ markerLabel(cp.text) }}
                </option>
              </select>
            </div>
            <!-- Bucket size -->
            <div>
              <label class="form-label mb-0 me-2 small fw-semibold">Bucket (min):</label>
              <select v-model.number="bucketSize" class="form-select form-select-sm d-inline-block" style="width: auto">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="15">15</option>
              </select>
            </div>
          </div>
          <div style="position: relative; height: 320px">
            <canvas ref="histogramCanvasRef"></canvas>
          </div>
          <div class="mt-2 d-flex align-items-center gap-2 small text-muted">
            <span class="legend-swatch" style="background: rgba(27,42,107,0.3)"></span> Field
            <span class="legend-swatch" style="background: #C23B7A"></span> Your bucket
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 3 — Checkpoint split table                             -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Checkpoint splits</div>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>Checkpoint</th>
                <th class="text-end">Elapsed</th>
                <th class="text-end">Segment</th>
                <th class="text-end">Overall rank</th>
                <th v-if="runnerCategory" class="text-end">Cat. rank</th>
                <th class="text-end">Δ pos.</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in splitTableRows"
                :key="row.name"
                :class="{ 'text-muted': !row.reached }"
              >
                <td>{{ row.name }}</td>
                <td class="text-end fw-semibold" :class="{ 'text-primary-brand': row.reached }">{{ row.elapsed }}</td>
                <td class="text-end">{{ row.segment }}</td>
                <td class="text-end">{{ row.rank != null ? row.rank : '—' }}</td>
                <td v-if="runnerCategory" class="text-end">{{ row.catRank != null ? row.catRank : '—' }}</td>
                <td class="text-end">
                  <span
                    v-if="row.delta != null && row.delta !== 0"
                    :class="row.delta > 0 ? 'text-success' : 'text-danger'"
                  >{{ row.delta > 0 ? `+${row.delta}` : row.delta }}</span>
                  <span v-else-if="row.reached && row.delta === 0" class="text-muted">—</span>
                  <span v-else>—</span>
                </td>
              </tr>
              <tr v-if="splitTableRows.length === 0">
                <td :colspan="runnerCategory ? 6 : 5" class="text-center text-muted">No checkpoint data</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 4 — Position progression chart                         -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div v-if="progressionPoints.length >= 2" class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Position progression</div>
        <div class="card-body">
          <div style="position: relative; height: 280px">
            <canvas ref="progressionCanvasRef"></canvas>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 5 — Pace per segment                                   -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div v-if="hasDistances && paceData && paceData.filter(s => s.runnerPace != null).length >= 2" class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Pace per segment</div>
        <div class="card-body">
          <div style="position: relative; height: 280px">
            <canvas ref="paceCanvasRef"></canvas>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 6 — Category comparison                                -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div v-if="categoryStats" class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Category comparison — {{ runnerCategory }}</div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-6 col-md-3">
              <div class="stat-block text-center">
                <div class="stat-value">{{ categoryStats.count }}</div>
                <div class="stat-label">Finishers in category</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="stat-block text-center">
                <div class="stat-value">{{ categoryStats.avg }}</div>
                <div class="stat-label">Category avg</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="stat-block text-center">
                <div class="stat-value">{{ categoryStats.median }}</div>
                <div class="stat-label">Category median</div>
              </div>
            </div>
            <div class="col-6 col-md-3">
              <div class="stat-block text-center">
                <div
                  class="stat-value"
                  :class="categoryStats.diffFromAvg <= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ categoryStats.diffFromAvg <= 0 ? '−' : '+' }}{{ formatElapsed(Math.abs(categoryStats.diffFromAvg)) }}
                </div>
                <div class="stat-label">vs avg</div>
              </div>
            </div>
          </div>
          <div class="mt-3 d-flex flex-column gap-1 small">
            <div v-if="categoryStats.aheadName">
              <span class="text-danger">▲ {{ formatElapsed(categoryStats.aheadGap) }} behind</span>
              {{ categoryStats.aheadName }} (#{{ categoryStats.aheadRank }} in category)
            </div>
            <div v-else class="text-success fw-semibold">🏆 Category leader!</div>
            <div v-if="categoryStats.behindName">
              <span class="text-success">▼ {{ formatElapsed(categoryStats.behindGap) }} ahead of</span>
              {{ categoryStats.behindName }}
            </div>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════ -->
      <!-- Section 7 — Finish time comparison chart                       -->
      <!-- ═══════════════════════════════════════════════════════════════ -->
      <div v-if="runnerStatus === 'FINISHED' && finishedRunners.length >= 2" class="card shadow-sm mb-4">
        <div class="card-header fw-semibold">Porównanie czasu ukończenia</div>
        <div class="card-body">
          <div style="position: relative; height: 280px">
            <canvas ref="finishTimeChartCanvasRef"></canvas>
          </div>
          <div class="mt-2 d-flex align-items-center gap-2 small text-muted">
            <span class="legend-swatch" style="background: rgba(27,42,107,0.3)"></span> inni
            <span class="legend-swatch" style="background: #C23B7A"></span> Ty
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<style scoped>
.runner-stats-page {
  text-align: left;
}

.bib-badge {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background: #C23B7A;
  color: #fff;
  font-size: 1.4rem;
  font-weight: 700;
  flex-shrink: 0;
}

.stat-block {
  min-width: 80px;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
}

.stat-label {
  font-size: 0.78rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status-finished  { background-color: #198754; }
.status-in-progress { background-color: #ffc107; color: #000; }
.status-dnf       { background-color: #dc3545; }

.text-primary-brand { color: #C23B7A; }

.btn-primary-brand {
  background-color: #C23B7A;
  border-color: #C23B7A;
  color: #fff;
}
.btn-primary-brand:hover {
  background-color: #a82f65;
  border-color: #a82f65;
  color: #fff;
}

.legend-swatch {
  display: inline-block;
  width: 16px;
  height: 12px;
  border-radius: 2px;
}

@media (max-width: 575px) {
  .stat-value { font-size: 1.25rem; }
  .bib-badge  { width: 52px; height: 52px; font-size: 1.1rem; }
}
</style>
